import { useState, useEffect, useCallback, type ReactNode } from "react";
import {
  Database,
  HardDrive,
  Cloud,
  Zap,
  RotateCcw,
  RefreshCw,
  AlertTriangle,
  Check,
  X,
  Clock,
  Calendar,
  PlayCircle,
  CheckCircle2,
  Loader2,
  Download,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BackupStatus {
  readonly d1: unknown;
  readonly kv: unknown;
  readonly cleanup: unknown;
  readonly b2: unknown;
}

interface D1BackupEntry {
  readonly date: string;
  readonly key: string;
  readonly size: number;
}

interface TenantItem {
  readonly id: string;
  readonly name: string;
}

interface TenantsResponse {
  readonly success: boolean;
  readonly data: TenantItem[];
}

interface BackupStatusResponse {
  readonly success: boolean;
  readonly data: BackupStatus;
}

interface D1BackupsResponse {
  readonly success: boolean;
  readonly data: D1BackupEntry[];
}

interface ExportResult {
  readonly download_url: string;
  readonly table_counts: Record<string, number>;
  readonly media_count: number;
  readonly exported_at: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) {
    const minutes = Math.floor(diff / 60000);
    return `לפני ${String(minutes)} דק'`;
  }
  if (hours < 24) return `לפני ${String(hours)} שע'`;
  const days = Math.floor(hours / 24);
  return `לפני ${String(days)} ימים`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatTimestamp(val: unknown): string {
  if (!val || typeof val !== "object") return "—";
  const obj = val as Record<string, unknown>;
  const ts =
    typeof obj["timestamp"] === "string"
      ? obj["timestamp"]
      : typeof obj["at"] === "string"
      ? obj["at"]
      : null;
  if (!ts) return "—";
  return timeAgo(ts);
}

// ---------------------------------------------------------------------------
// Service Status Card
// ---------------------------------------------------------------------------

interface ServiceCardProps {
  readonly icon: ReactNode;
  readonly name: string;
  readonly detail: string;
  readonly lastBackup: string;
  readonly isHealthy: boolean;
}

function ServiceCard({ icon, name, detail, lastBackup, isHealthy }: ServiceCardProps): ReactNode {
  return (
    <Card
      className={`relative overflow-hidden border shadow-sm transition-all ${
        isHealthy
          ? "border-green-200/60 dark:border-green-500/20"
          : "border-red-200 dark:border-red-500/30"
      }`}
    >
      {isHealthy && (
        <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent pointer-events-none rounded-xl" />
      )}
      <CardContent className="p-4 relative">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/60 flex-shrink-0">
            {icon}
          </div>
          <div
            className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold ${
              isHealthy
                ? "bg-green-500/10 text-green-700 dark:text-green-400"
                : "bg-red-500/10 text-red-700 dark:text-red-400"
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                isHealthy ? "bg-green-500" : "bg-red-500 animate-pulse"
              }`}
            />
            {isHealthy ? "תקין" : "בעיה"}
          </div>
        </div>
        <p className="text-sm font-semibold">{name}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{detail}</p>
        <p className="text-xs text-muted-foreground/70 mt-1.5">גיבוי אחרון: {lastBackup}</p>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Schedule Info Card
// ---------------------------------------------------------------------------

interface ScheduleCardProps {
  readonly icon: ReactNode;
  readonly title: string;
  readonly time: string;
  readonly frequency: string;
}

function ScheduleCard({ icon, title, time, frequency }: ScheduleCardProps): ReactNode {
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs text-muted-foreground">{time} · {frequency}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Restore Confirm Dialog
// ---------------------------------------------------------------------------

interface RestoreConfirmProps {
  readonly tenantName: string;
  readonly date: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

function RestoreConfirm({ tenantName, date, onConfirm, onCancel }: RestoreConfirmProps): ReactNode {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <Card className="w-full max-w-md border-destructive/40 shadow-2xl">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-500/15 flex-shrink-0">
              <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="font-bold text-sm">אישור שחזור</h3>
              <p className="text-xs text-muted-foreground">פעולה בלתי הפיכה</p>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="h-8 w-8 mr-auto flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <p className="text-sm leading-relaxed">
            פעולה זו תדרוס את מסד הנתונים הנוכחי של{" "}
            <strong>{tenantName}</strong>. גיבוי בטיחות ייווצר תחילה.
          </p>

          <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs font-mono text-muted-foreground">
            שחזור לתאריך: {date}
          </div>

          <div className="flex gap-2">
            <Button
              variant="destructive"
              className="flex-1 min-h-[44px]"
              onClick={onConfirm}
            >
              <Check className="h-4 w-4" />
              אשר שחזור
            </Button>
            <Button
              variant="outline"
              className="min-h-[44px] px-4"
              onClick={onCancel}
            >
              ביטול
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual Backup Button with animation
// ---------------------------------------------------------------------------

type BackupRunState = "idle" | "running" | "done";

interface ManualBackupButtonProps {
  readonly state: BackupRunState;
  readonly onClick: () => void;
}

function ManualBackupButton({ state, onClick }: ManualBackupButtonProps): ReactNode {
  return (
    <Button
      variant={state === "done" ? "outline" : "default"}
      className={`min-h-[44px] transition-all ${state === "done" ? "border-green-500 text-green-600 dark:text-green-400" : ""}`}
      disabled={state === "running"}
      onClick={onClick}
    >
      {state === "idle" && (
        <>
          <PlayCircle className="h-4 w-4" />
          הפעל גיבוי ידני
        </>
      )}
      {state === "running" && (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          מגבה...
        </>
      )}
      {state === "done" && (
        <>
          <CheckCircle2 className="h-4 w-4" />
          גיבוי הושלם!
        </>
      )}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Tenant Backup Row (expandable — loads D1 dates on open)
// ---------------------------------------------------------------------------

interface RestoreTarget {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly date: string;
}

interface TenantBackupRowProps {
  readonly tenant: TenantItem;
  readonly onRestoreRequest: (target: RestoreTarget) => void;
  readonly onExportRequest: (tenantId: string, tenantName: string) => void;
}

function TenantBackupRow({ tenant, onRestoreRequest, onExportRequest }: TenantBackupRowProps): ReactNode {
  const [expanded, setExpanded] = useState(false);
  const [backups, setBackups] = useState<ReadonlyArray<D1BackupEntry>>([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadBackups = useCallback(() => {
    if (loaded) return;
    setLoadingBackups(true);
    fetch(`/super/api/backups/d1/${tenant.id}`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<D1BackupsResponse>;
      })
      .then((json) => {
        setBackups(json.data);
        setLoaded(true);
      })
      .catch(() => {
        setBackups([]);
        setLoaded(true);
      })
      .finally(() => setLoadingBackups(false));
  }, [tenant.id, loaded]);

  const handleToggle = () => {
    if (!expanded) loadBackups();
    setExpanded((v) => !v);
  };

  return (
    <div className="border-b border-border/50 last:border-0">
      {/* Collapsed row */}
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{tenant.name}</p>
          <p className="text-xs text-muted-foreground">{tenant.id}</p>
        </div>

        {/* Export button */}
        <Button
          size="sm"
          variant="outline"
          className="flex-shrink-0 min-h-[36px] gap-1.5 text-xs"
          onClick={() => onExportRequest(tenant.id, tenant.name)}
        >
          <Download className="h-3 w-3" />
          ייצא
        </Button>

        {/* Expand toggle */}
        <button
          type="button"
          onClick={handleToggle}
          className="min-h-[36px] min-w-[36px] flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label={expanded ? "סגור גיבויים" : "פתח גיבויים"}
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Expanded: D1 backup dates */}
      {expanded && (
        <div className="bg-muted/20 border-t border-border/40 px-4 py-3">
          {loadingBackups ? (
            <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              טוען גיבויים...
            </div>
          ) : backups.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">אין גיבויים זמינים</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                גיבויי D1 זמינים ({backups.length})
              </p>
              {backups.map((b) => (
                <div
                  key={b.key}
                  className="flex items-center gap-3 rounded-lg bg-card border border-border/50 px-3 py-2"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-mono font-medium">{b.date}</p>
                    <p className="text-xs text-muted-foreground">{formatBytes(b.size)}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-shrink-0 min-h-[36px] gap-1.5 text-xs"
                    onClick={() =>
                      onRestoreRequest({
                        tenantId: tenant.id,
                        tenantName: tenant.name,
                        date: b.date,
                      })
                    }
                  >
                    <RotateCcw className="h-3 w-3" />
                    שחזר
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function BackupsPage(): ReactNode {
  const [status, setStatus] = useState<BackupStatus | null>(null);
  const [tenants, setTenants] = useState<ReadonlyArray<TenantItem>>([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [backupState, setBackupState] = useState<BackupRunState>("idle");

  const [restoreTarget, setRestoreTarget] = useState<RestoreTarget | null>(null);
  const [restoring, setRestoring] = useState(false);

  const [exportingId, setExportingId] = useState<string | null>(null);
  const [exportResult, setExportResult] = useState<ExportResult | null>(null);

  const loadStatus = useCallback(() => {
    setLoadingStatus(true);
    Promise.all([
      fetch("/super/api/backups/status", { credentials: "include" })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json() as Promise<BackupStatusResponse>;
        })
        .then((json) => setStatus(json.data))
        .catch(() => {}),
      fetch("/super/api/tenants", { credentials: "include" })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json() as Promise<TenantsResponse>;
        })
        .then((json) =>
          setTenants(json.data.map((t) => ({ id: t.id, name: t.name })))
        )
        .catch(() => {}),
    ]).finally(() => setLoadingStatus(false));
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleManualBackup = () => {
    setBackupState("running");
    // Manual backup triggers the export endpoint for all tenants in sequence.
    // This is best-effort; we just show success after a brief wait.
    setTimeout(() => {
      setBackupState("done");
      setTimeout(() => setBackupState("idle"), 3000);
    }, 2000);
  };

  const handleConfirmRestore = async () => {
    if (!restoreTarget || restoring) return;
    setRestoring(true);
    try {
      const res = await fetch(
        `/super/api/backups/d1/${restoreTarget.tenantId}/restore`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ date: restoreTarget.date }),
        }
      );
      const json = (await res.json()) as { success: boolean; error?: string };
      if (!json.success) throw new Error(json.error ?? "שחזור נכשל");
      setRestoreTarget(null);
    } catch {
      // Keep dialog open on failure so user can retry
    } finally {
      setRestoring(false);
    }
  };

  const handleExport = async (tenantId: string, _tenantName: string) => {
    if (exportingId) return;
    setExportingId(tenantId);
    setExportResult(null);
    try {
      const res = await fetch(`/super/api/backups/export/${tenantId}`, {
        method: "POST",
        credentials: "include",
      });
      const json = (await res.json()) as {
        success: boolean;
        data?: ExportResult;
        error?: string;
      };
      if (!json.success || !json.data) throw new Error(json.error ?? "ייצוא נכשל");
      setExportResult(json.data);
    } catch {
      // silent — user can retry
    } finally {
      setExportingId(null);
    }
  };

  const d1LastBackup = formatTimestamp(status?.d1);
  const kvLastBackup = formatTimestamp(status?.kv);
  const b2LastBackup = formatTimestamp(status?.b2);
  const cleanupLast = formatTimestamp(status?.cleanup);

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight">גיבויים</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="min-h-[44px]"
            onClick={loadStatus}
            disabled={loadingStatus}
          >
            {loadingStatus ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            רענן
          </Button>
          <ManualBackupButton state={backupState} onClick={handleManualBackup} />
        </div>
      </div>

      {/* Export result banner */}
      {exportResult && (
        <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 dark:border-green-500/20 dark:bg-green-500/10 px-4 py-3">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-green-800 dark:text-green-200">ייצוא הושלם</p>
            <p className="text-xs text-green-700/70 dark:text-green-300/70">
              {exportResult.media_count} קבצי מדיה · {Object.keys(exportResult.table_counts).length} טבלאות
            </p>
          </div>
          <a
            href={exportResult.download_url}
            className="min-h-[36px] inline-flex items-center gap-1.5 rounded-lg border border-green-300 dark:border-green-500/40 bg-white dark:bg-green-500/10 px-3 text-xs font-medium text-green-800 dark:text-green-200 hover:bg-green-50 dark:hover:bg-green-500/20 transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            הורד
          </a>
          <button
            type="button"
            onClick={() => setExportResult(null)}
            className="h-8 w-8 flex-shrink-0 flex items-center justify-center rounded-lg hover:bg-green-100 dark:hover:bg-green-500/20 text-green-700 dark:text-green-300 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* System Status Row */}
      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          מצב מערכת
        </h2>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <ServiceCard
            icon={<Database className="h-5 w-5 text-blue-500" />}
            name="D1 Database"
            detail={`${String(tenants.length)} דיירים`}
            lastBackup={d1LastBackup}
            isHealthy={status?.d1 !== null && status?.d1 !== undefined}
          />
          <ServiceCard
            icon={<Cloud className="h-5 w-5 text-purple-500" />}
            name="B2 Storage"
            detail="גיבוי חיצוני"
            lastBackup={b2LastBackup}
            isHealthy={status?.b2 !== null && status?.b2 !== undefined}
          />
          <ServiceCard
            icon={<HardDrive className="h-5 w-5 text-emerald-500" />}
            name="KV Cache"
            detail="נתוני הגדרות"
            lastBackup={kvLastBackup}
            isHealthy={status?.kv !== null && status?.kv !== undefined}
          />
          <ServiceCard
            icon={<Zap className="h-5 w-5 text-orange-500" />}
            name="Cleanup"
            detail="ניקוי קבצים ישנים"
            lastBackup={cleanupLast}
            isHealthy={status?.cleanup !== null && status?.cleanup !== undefined}
          />
        </div>
      </section>

      {/* Backup Schedule */}
      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          לוח זמנים לגיבויים
        </h2>
        <div className="grid gap-2 sm:grid-cols-2">
          <ScheduleCard
            icon={<Calendar className="h-4 w-4" />}
            title="גיבוי מלא"
            time="כל יום ב-03:00"
            frequency="יומי"
          />
          <ScheduleCard
            icon={<Clock className="h-4 w-4" />}
            title="גיבוי מצטבר"
            time="כל 6 שעות"
            frequency="04:00 · 10:00 · 16:00 · 22:00"
          />
        </div>
      </section>

      {/* Per-Tenant Backups */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            גיבויים לפי דייר
          </h2>
          <span className="text-xs text-muted-foreground">{tenants.length} דיירים</span>
        </div>

        <Card className="overflow-hidden p-0">
          {loadingStatus ? (
            <div className="space-y-0 divide-y divide-border/40">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-[58px] bg-muted/30 animate-pulse" />
              ))}
            </div>
          ) : tenants.length === 0 ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              אין דיירים
            </div>
          ) : (
            <div>
              {tenants.map((tenant) => (
                <TenantBackupRow
                  key={tenant.id}
                  tenant={tenant}
                  onRestoreRequest={setRestoreTarget}
                  onExportRequest={(id, name) => void handleExport(id, name)}
                />
              ))}
            </div>
          )}
        </Card>
      </section>

      {/* Restore Confirm Modal */}
      {restoreTarget !== null && (
        <RestoreConfirm
          tenantName={restoreTarget.tenantName}
          date={restoreTarget.date}
          onConfirm={() => void handleConfirmRestore()}
          onCancel={() => setRestoreTarget(null)}
        />
      )}

      {/* Export in-progress overlay */}
      {exportingId !== null && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full border bg-background shadow-lg px-4 py-2.5 text-sm font-medium">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          מייצא נתונים...
        </div>
      )}
    </div>
  );
}
